<ng-template
  #hourSegmentTemplate
  let-segment="segment"
  let-locale="locale"
  let-segmentHeight="segmentHeight"
  let-isTimeLabel="isTimeLabel"
  let-daysInWeek="daysInWeek"
>
  <div
    class="cal-hour-segment"
    [attr.aria-hidden]="{} | calendarA11y: (daysInWeek === 1 ? 'hideDayHourSegment' : 'hideWeekHourSegment')"
    [style.height.px]="segmentHeight"
    [class.cal-hour-start]="segment.isStart"
    [class.cal-after-hour-start]="!segment.isStart"
    [ngClass]="segment.cssClass"
    (dblclick)="onHourSegmentClicked(segment, $event)"
  >
    @if (isTimeLabel) {
      <div class="cal-time">
        {{ segment.displayDate | calendarDate: (daysInWeek === 1 ? 'dayViewHour' : 'weekViewHour') : locale }}
      </div>
    }
  </div>
</ng-template>

<ng-template #monthEventTitleTemplate let-event="event" let-view="view">
  <span class="cal-event-title" [attr.aria-hidden]="{} | calendarA11y: 'hideEventTitle'">
    <div class="event-title-container" [class.no-icon]="!event?.meta?.isRecurrent">
      @if (event?.meta?.isRecurrent) {
        <i class="recurrent-icon" [nimbusIcon]="event?.meta?.recurrentException ? ICONS.RECURRENCE_EXCEPTION : ICONS.RECURRENCE"></i>
      }
      {{ event.title | calendarEventTitle: view : event }}
    </div>
    {{
      event.allDay ? (TranslationKeys.GeneralAllDay | translate) : (event.start | dateFormat: 'LT') + ' - ' + (event.end | dateFormat: 'LT')
    }}
  </span>
</ng-template>

<ng-template #eventTitleTemplate let-event="event" let-view="view">
  <span class="cal-event-title" [class.all-day-event-title]="event?.allDay" [attr.aria-hidden]="{} | calendarA11y: 'hideEventTitle'">
    @if (!event?.meta?.showDateInHeader) {
      <div class="call-event-title-base">
        @if (event?.meta?.isRecurrent) {
          <i class="recurrent-icon" [nimbusIcon]="event?.meta?.recurrentException ? ICONS.RECURRENCE_EXCEPTION : ICONS.RECURRENCE"></i>
        }
        {{ event.title | calendarEventTitle: view : event }}
      </div>
      @if (event?.meta?.location) {
        <span class="call-event-location"> {{ event.meta.location }} </span>
      }
    } @else {
      <div class="title-in-header">
        <div class="call-event-title-base all-day-event-title">
          @if (event?.meta?.isRecurrent) {
            <i class="recurrent-icon" [nimbusIcon]="event?.meta?.recurrentException ? ICONS.RECURRENCE_EXCEPTION : ICONS.RECURRENCE"></i>
          }
          <span class="title-event-start">{{ event.start | dateFormat: 'LT' }}</span>
          <span class="title-event">{{ event.title | calendarEventTitle: view : event }}</span>
          <span class="title-event-end">{{ event.end | dateFormat: 'LT' }}</span>
        </div>
      </div>
      @if (event?.meta?.location) {
        <span class="call-event-location">{{ event.meta.location }}</span>
      }
    }
  </span>
</ng-template>

<!-- `track` cannot read let- context variables, so the library's trackBy functions are inlined -->
<ng-template #eventActionsTemplate let-event="event">
  @if (event.actions) {
    <span class="cal-event-actions">
      @for (action of event.actions; track action.id || action) {
        <a
          class="cal-event-action"
          href="javascript:;"
          tabindex="0"
          role="button"
          [ngClass]="action.cssClass"
          [attr.aria-label]="{ action: action } | calendarA11y: 'actionButtonLabel'"
          (mwlClick)="action.onClick({ event: event, sourceEvent: $event })"
          (mwlKeydownEnter)="action.onClick({ event: event, sourceEvent: $event })"
        >
          <span [innerHtml]="action.label"></span>
        </a>
      }
    </span>
  }
</ng-template>

<ng-template
  #hourEventTemplate
  let-weekEvent="weekEvent"
  let-tooltipPlacement="tooltipPlacement"
  let-eventClicked="eventClicked"
  let-tooltipTemplate="tooltipTemplate"
  let-tooltipAppendToBody="tooltipAppendToBody"
  let-tooltipDisabled="tooltipDisabled"
  let-tooltipDelay="tooltipDelay"
  let-column="column"
  let-daysInWeek="daysInWeek"
>
  <nimbus-overlay-entity-details
    customClass="nimbus-calendar-tooltip cdk-overlay-delay"
    [showEmptyValues]="true"
    [entityConfiguration]="tooltipMetadataConfiguration"
    [emptyPlaceholder]="TranslationKeys.GeneralEmpty"
    [hideCopyFunctionality]="true"
    [showEmptyValues]="!hideLocation"
    [showOnHover]="true"
    [entity]="{
      subject: weekEvent.event?.title,
      location: !hideLocation ? weekEvent.event?.meta?.location : undefined,
      start: weekEvent.event?.start | dateFormat: 'LLL',
      end: weekEvent.event?.end | dateFormat: 'LLL',
    }"
  >
    <div
      class="cal-event"
      tabindex="0"
      role="application"
      [attr.aria-label]="
        {
          event: weekEvent.tempEvent || weekEvent.event,
          locale: locale,
        } | calendarA11y: 'eventDescription'
      "
      [ngStyle]="{
        backgroundColor: weekEvent.event.color?.secondary,
        borderColor: weekEvent.event.color?.primary,
      }"
      (dblclick)="eventClicked.emit({ sourceEvent: $event })"
    >
      <mwl-calendar-event-actions
        [event]="weekEvent.tempEvent || weekEvent.event"
        [customTemplate]="eventActionsTemplate"
      ></mwl-calendar-event-actions>
      &ngsp;
      <mwl-calendar-event-title
        [event]="weekEvent.tempEvent || weekEvent.event"
        [customTemplate]="eventTitleTemplate"
        [view]="daysInWeek === 1 ? 'day' : 'week'"
        [tooltipTemplate]="tooltipTemplate"
      ></mwl-calendar-event-title>
    </div>
  </nimbus-overlay-entity-details>
</ng-template>

<ng-template
  #monthDayTemplate
  let-day="day"
  let-openDay="openDay"
  let-locale="locale"
  let-tooltipPlacement="tooltipPlacement"
  let-highlightDay="highlightDay"
  let-unhighlightDay="unhighlightDay"
  let-eventClicked="eventClicked"
  let-tooltipTemplate="tooltipTemplate"
  let-tooltipAppendToBody="tooltipAppendToBody"
  let-tooltipDelay="tooltipDelay"
  let-validateDrag="validateDrag"
>
  <div class="cal-cell-top" [attr.aria-label]="{ day: day, locale: locale } | calendarA11y: 'monthCell'">
    <span aria-hidden="true">
      <span class="cal-day-number">
        {{ day.date.getDate() === 1 ? (day.date | dateFormat: 'MMM, D' : false) : (day.date | dateFormat: 'D' : false) }}
      </span>
    </span>
  </div>
  @if (day.events.length > 0) {
    <div class="cal-events">
      @for (event of day.events; track event.id || event) {
        <div class="cal-event-container" [ngClass]="event?.cssClass">
          <nimbus-overlay-entity-details
            customClass="nimbus-calendar-tooltip cdk-overlay-delay"
            [showEmptyValues]="true"
            [entityConfiguration]="tooltipMetadataConfiguration"
            [emptyPlaceholder]="TranslationKeys.GeneralEmpty"
            [hideCopyFunctionality]="true"
            [showEmptyValues]="!hideLocation"
            [showOnHover]="true"
            [entity]="{
              subject: event?.title,
              location: !hideLocation ? event?.meta?.location : undefined,
              start: event?.start | dateFormat: 'LLL',
              end: event?.end | dateFormat: 'LLL',
            }"
          >
            <div
              class="cal-event"
              tabindex="0"
              role="application"
              [attr.aria-label]="{ event: event, locale: locale } | calendarA11y: 'eventDescription'"
              [ngStyle]="{
                backgroundColor: event?.color?.secondary,
                borderColor: event?.color?.primary,
              }"
              (dblclick)="eventClicked.emit({ event: event, sourceEvent: $event })"
            >
              <mwl-calendar-event-actions [event]="event" [customTemplate]="eventActionsTemplate"></mwl-calendar-event-actions>
              &ngsp;
              <mwl-calendar-event-title [event]="event" [customTemplate]="monthEventTitleTemplate" [view]="'month'">
              </mwl-calendar-event-title>
            </div>
          </nimbus-overlay-entity-details>
        </div>
      }
    </div>
  }
</ng-template>

<ng-template
  #monthEventList
  let-events="events"
  let-eventClicked="eventClicked"
  let-isOpen="isOpen"
  let-trackByEventId="trackByEventId"
  let-validateDrag="validateDrag"
></ng-template>

<ng-template
  #dayHeaderTemplate
  let-days="days"
  let-locale="locale"
  let-dayHeaderClicked="dayHeaderClicked"
  let-eventDropped="eventDropped"
  let-dragEnter="dragEnter"
>
  <div class="cal-day-headers" role="row">
    @for (day of days; track day.date.toISOString()) {
      <div
        class="cal-header cal-clickable-header"
        tabindex="0"
        role="columnheader"
        dragOverClass="cal-drag-over"
        [ngClass]="day.cssClass"
        [class.cal-past]="day.isPast"
        [class.cal-today]="day.isToday"
        [class.cal-future]="day.isFuture"
        [class.cal-weekend]="day.isWeekend"
        (dblclick)="onAllDayClicked(day, $event)"
        (mwlClick)="dayHeaderClicked.emit({ day: day, sourceEvent: $event })"
        (dragEnter)="dragEnter.emit({ date: day.date })"
        (drop)="
          eventDropped.emit({
            event: $event.dropData.event,
            newStart: day.date,
          })
        "
        mwlDroppable
      >
        <b>{{ day.date | calendarDate: 'weekViewColumnHeader' : locale }}</b>
        <br />
        <span>{{ day.date | calendarDate: 'weekViewColumnSubHeader' : locale }}</span>
      </div>
    }
  </div>
</ng-template>

<div>
  @switch (view) {
    @case ('month') {
      <mwl-calendar-month-view
        [viewDate]="viewDate"
        [events]="calendarEvents$ | async"
        [cellTemplate]="monthDayTemplate"
        [openDayEventsTemplate]="monthEventList"
        [activeDayIsOpen]="true"
        (dayClicked)="dayClicked($event.day)"
        (beforeViewRender)="updateCalendarEvents($event)"
        (eventClicked)="onEventClicked($event)"
      ></mwl-calendar-month-view>
    }
    @case ('week') {
      <mwl-calendar-week-view
        [viewDate]="viewDate"
        [events]="calendarEvents$ | async"
        [dayStartHour]="dayStartHour"
        [dayEndHour]="dayEndHour"
        [headerTemplate]="dayHeaderTemplate"
        [hourSegmentTemplate]="hourSegmentTemplate"
        [eventTemplate]="hourEventTemplate"
        [minimumEventHeight]="15"
        (beforeViewRender)="updateCalendarEvents($event)"
        (eventTimesChanged)="onEventTimesChanged($event)"
        (eventClicked)="onEventClicked($event)"
      ></mwl-calendar-week-view>
    }
    @case ('day') {
      <mwl-calendar-day-view
        [viewDate]="viewDate"
        [events]="calendarEvents$ | async"
        [hourSegmentTemplate]="hourSegmentTemplate"
        [dayStartHour]="dayStartHour"
        [dayEndHour]="dayEndHour"
        [eventTemplate]="hourEventTemplate"
        [minimumEventHeight]="15"
        (eventTimesChanged)="onEventTimesChanged($event)"
        (eventClicked)="onEventClicked($event)"
        (beforeViewRender)="updateCalendarEvents($event)"
      ></mwl-calendar-day-view>
    }
  }
</div>
